import { useState } from 'react'
import { useQuery } from '@apollo/client'
import { Link, useNavigate } from 'react-router-dom'
import { 
  PlusIcon, 
  MagnifyingGlassIcon,
  CogIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'
import { GET_OPTION_GROUPS } from '../graphql/product-queries'
// import { DELETE_OPTION_GROUP } from '../graphql/mutations'
import { OptionGroup, OptionGroupFilters } from '../types/product'
import LoadingSpinner from '../components/LoadingSpinner'
// import toast from 'react-hot-toast'

const OptionGroupsPage = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined)

  const filters: OptionGroupFilters = {
    search: searchTerm || undefined,
    active: activeFilter
  }

  const { data, loading, error, refetch } = useQuery(GET_OPTION_GROUPS, {
    variables: { filters }
  })

  // const [deleteOptionGroup] = useMutation(DELETE_OPTION_GROUP, {
  //   onCompleted: () => {
  //     toast.success('Option group deleted successfully')
  //     refetch()
  //   },
  //   onError: (error) => {
  //     toast.error(`Failed to delete option group: ${error.message}`)
  //   }
  // })

  const optionGroups = data?.optionGroups || []

  const handleSearch = (value: string) => {
    setSearchTerm(value)
  }

  const handleActiveFilterChange = (value: boolean | undefined) => {
    setActiveFilter(value)
  }

  // const handleDelete = async (id: number, name: string) => {
  //   if (window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
  //     try {
  //       await deleteOptionGroup({
  //         variables: { id }
  //       })
  //     } catch (error) {
  //       // Error is handled in the mutation's onError callback
  //     }
  //   }
  // }

  const handleRowClick = (optionGroupId: number) => {
    navigate(`/option-groups/${optionGroupId}`)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">⚠️</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading option groups</h3>
        <p className="text-gray-500 mb-6">{error.message}</p>
        <button 
          onClick={() => refetch()}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Option Groups</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage product customization options like toppings and add-ons
          </p>
        </div>
        <Link
          to="/option-groups/new"
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Option Group
        </Link>
      </div>

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search option groups..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Active Filter */}
          <div className="flex border border-gray-300 rounded-lg">
            <button
              onClick={() => handleActiveFilterChange(undefined)}
              className={`px-3 py-2 ${activeFilter === undefined ? 'bg-primary-500 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              All
            </button>
            <button
              onClick={() => handleActiveFilterChange(true)}
              className={`px-3 py-2 ${activeFilter === true ? 'bg-primary-500 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              Active
            </button>
            <button
              onClick={() => handleActiveFilterChange(false)}
              className={`px-3 py-2 ${activeFilter === false ? 'bg-primary-500 text-white' : 'text-gray-600 hover:text-gray-900'}`}
            >
              Inactive
            </button>
          </div>
        </div>
      </div>

      {/* Option Groups Table */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Selection Rules
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ask During Add
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sort Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {optionGroups.map((optionGroup: OptionGroup) => (
                <tr 
                  key={optionGroup.id} 
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleRowClick(optionGroup.id)}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <CogIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{optionGroup.name}</div>
                        <div className="text-sm text-gray-500">ID: {optionGroup.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center space-x-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Min: {optionGroup.min_select}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Max: {optionGroup.max_select}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {optionGroup.ask_during_add ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Yes
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        No
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {optionGroup.sort_order || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {optionGroup.active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <XCircleIcon className="h-3 w-3 mr-1" />
                        Inactive
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {optionGroups.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">⚙️</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No option groups found</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || activeFilter !== undefined
              ? 'Try adjusting your search or filters'
              : 'Get started by creating your first option group'
            }
          </p>
          {!searchTerm && activeFilter === undefined && (
            <Link to="/option-groups/new" className="btn-primary">
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Option Group
            </Link>
          )}
        </div>
      )}
    </div>
  )
}

export default OptionGroupsPage
